import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    // Smooth scroll
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    // Header scroll effect with hide/show functionality
    let ticking = false;
    let scrollTimeout = null;

    const handleScroll = () => {
      // Marcar que está fazendo scroll
      setIsScrolling(true);
      
      // Limpar timeout anterior
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      // Definir timeout para detectar quando o scroll parou
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 150);

      if (!ticking) {
        window.requestAnimationFrame(() => {
          const header = document.querySelector('.header');
          if (!header) {
            ticking = false;
            return;
          }

          const currentScrollY = window.pageYOffset;
          const scrollDifference = Math.abs(currentScrollY - lastScrollY);

          // Se estiver no topo (próximo de 0), sempre mostrar o header
          if (currentScrollY < 20) {
            setIsHeaderVisible(true);
            header.style.background = 'rgba(10, 10, 10, 0.95)';
            header.style.boxShadow = 'none';
            setLastScrollY(currentScrollY);
            ticking = false;
            return;
          }

          // Só processar se a diferença de scroll for significativa (evita flickering)
          if (scrollDifference < 5) {
            ticking = false;
            return;
          }

          // Atualizar background e shadow baseado no scroll
          if (currentScrollY > 100) {
            header.style.background = 'rgba(10, 10, 10, 0.98)';
            header.style.boxShadow = '0 4px 20px rgba(197, 165, 114, 0.1)';
          } else {
            header.style.background = 'rgba(10, 10, 10, 0.95)';
            header.style.boxShadow = 'none';
          }

          // Se estiver rolando para baixo e já passou do threshold, esconder
          if (currentScrollY > lastScrollY && currentScrollY > 80) {
            setIsHeaderVisible(false);
          } 
          // Se estiver rolando para cima, mostrar
          else if (currentScrollY < lastScrollY) {
            setIsHeaderVisible(true);
          }

          setLastScrollY(currentScrollY);
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    // Observe all feature cards
    document.querySelectorAll('.feature-card').forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(30px)';
      card.style.transition = 'opacity 0.6s, transform 0.6s';
      observer.observe(card);
    });

    // Observe pricing cards
    document.querySelectorAll('.pricing-card').forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(30px)';
      card.style.transition = 'opacity 0.6s, transform 0.6s';
      observer.observe(card);
    });

    // Hero quotes animation
    const heroQuotes = [
      "Sucesso é ir de fracasso em fracasso sem perder o entusiasmo.",
      "Se você pode sonhar, pode fazer.",
      "Ação é a chave fundamental para todo sucesso.",
      "Feito é melhor que perfeito.",
      "A melhor forma de prever o futuro é criá-lo.",
      "Não encontre defeitos; encontre soluções.",
      "Oportunidades não acontecem. Você as cria.",
      "A sorte favorece a mente preparada.",
      "No meio da dificuldade está a oportunidade.",
      "A persistência é o caminho do êxito.",
      "Disciplina é a ponte entre metas e realizações.",
      "O sucesso é a soma de pequenos esforços repetidos diariamente.",
      "Comece onde você está. Use o que tem. Faça o que puder.",
      "Você perde 100% dos arremessos que não tenta.",
      "A jornada de mil milhas começa com um passo.",
      "A excelência não é um ato, é um hábito.",
      "Sorte é quando a preparação encontra a oportunidade.",
      "Acredite que você pode e você já está no meio do caminho.",
      "Foco é dizer não.",
      "Continue faminto. Continue tolo.",
      "Eu não falhei. Só achei 10.000 maneiras que não funcionam.",
      "Nossa maior fraqueza é desistir.",
      "O sucesso exige ação.",
      "Sonhe grande. Comece pequeno. Aja agora.",
      "O impossível é só opinião.",
      "Coragem é resistência ao medo, domínio do medo.",
      "O segredo de progredir é começar.",
      "Aquilo que não nos mata nos fortalece.",
      "A criatividade é inteligência se divertindo.",
      "A fortuna favorece os audazes.",
      "O sucesso depende de preparação.",
      "A persistência transforma o impossível em possível.",
      "Consistência vence intensidade.",
      "A vitória ama a preparação.",
      "A maior glória é levantar após cair.",
      "Sempre parece impossível até ser feito.",
      "O sucesso não é definitivo; o fracasso não é fatal.",
      "A melhor forma de aprender é fazer.",
      "A inovação distingue um líder de um seguidor.",
      "Trabalhe duro em silêncio; deixe o sucesso fazer barulho.",
      "A única forma de fazer um ótimo trabalho é amar o que faz.",
      "Não conte os dias; faça os dias contarem.",
      "Impossível é nada.",
      "O medo mata mais sonhos que o fracasso.",
      "A coragem é a primeira das qualidades humanas.",
      "A sorte favorece quem se prepara.",
      "Pequenos passos todo dia.",
      "Objetivos claros. Ação imediata.",
      "Sua vida só melhora quando você melhora.",
      "A motivação te inicia; o hábito te mantém.",
      "Fracasso é sucesso em progresso.",
      "Não espere. O tempo nunca será 'perfeito'.",
      "O entusiasmo move o mundo.",
      "Onde há vontade, há caminho.",
      "A melhor maneira de vencer é não parar."
    ];

    let heroDraggedQuote = null;
    let heroDragOffset = { x: 0, y: 0 };
    let heroIsDragging = false;

    function createHeroQuote() {
      const heroQuotesAnimation = document.getElementById('heroQuotesAnimation');
      if (!heroQuotesAnimation) return;

      // Contar frases visíveis (não arrastadas e sem posição manual)
      const allQuotes = heroQuotesAnimation.querySelectorAll('.hero-quote');
      const visibleQuotes = Array.from(allQuotes).filter(quote => {
        return !quote.classList.contains('dragging') && 
               quote.dataset.manualPosition !== 'true' &&
               quote.parentNode === heroQuotesAnimation;
      });

      // Se já temos 5 ou mais frases visíveis, remover as mais antigas
      // Mas manter pelo menos 2 frases
      if (visibleQuotes.length >= 5) {
        // Ordenar por timestamp de criação (mais antigas primeiro)
        const sortedQuotes = visibleQuotes.sort((a, b) => {
          const timeA = parseInt(a.dataset.createdAt) || 0;
          const timeB = parseInt(b.dataset.createdAt) || 0;
          return timeA - timeB;
        });
        
        // Remover as mais antigas, mantendo apenas as 2 mais recentes
        const quotesToRemove = sortedQuotes.slice(0, sortedQuotes.length - 2);
        quotesToRemove.forEach(quote => {
          if (quote.parentNode && !quote.dataset.manualPosition) {
            quote.parentNode.removeChild(quote);
          }
        });
      }

      // Garantir que temos pelo menos 2 frases visíveis
      const currentVisibleCount = heroQuotesAnimation.querySelectorAll('.hero-quote:not(.dragging):not([data-manual-position="true"])').length;
      if (currentVisibleCount < 2 && allQuotes.length < 2) {
        // Criar frases até ter pelo menos 2
        const needed = 2 - currentVisibleCount;
        for (let i = 0; i < needed; i++) {
          createSingleQuote();
        }
        return;
      }

      // Criar nova frase normalmente
      createSingleQuote();
    }

    function createSingleQuote() {
      const heroQuotesAnimation = document.getElementById('heroQuotesAnimation');
      if (!heroQuotesAnimation) return;

      const quote = document.createElement('div');
      quote.className = 'hero-quote';
      const quoteText = heroQuotes[Math.floor(Math.random() * heroQuotes.length)];
      quote.innerHTML = '"' + quoteText + '"';

      const leftPosition = Math.random() * 100;
      quote.style.left = leftPosition + '%';

      const duration = 18 + Math.random() * 5;
      quote.style.animationDuration = duration + 's';
      quote.dataset.originalDuration = duration;
      quote.dataset.createdAt = Date.now(); // Timestamp para ordenação

      const drift = (Math.random() - 0.5) * 30;
      quote.style.setProperty('--drift', drift + 'px');

      quote.addEventListener('mousedown', function (e) {
        if (e.button !== 0) return;
        const rect = quote.getBoundingClientRect();
        quote.style.animation = 'none';
        quote.style.top = rect.top + 'px';
        quote.style.left = rect.left + 'px';
        quote.classList.add('selected');
        quote.dataset.mouseDownX = e.clientX;
        quote.dataset.mouseDownY = e.clientY;
        quote.dataset.hasMoved = 'false';
        quote.dataset.isPotentialDrag = 'true';
        quote.dataset.clickTop = rect.top;
        quote.dataset.clickLeft = rect.left;
        e.preventDefault();
        e.stopPropagation();
      });

      heroQuotesAnimation.appendChild(quote);

      // Remover automaticamente após a animação (se não tiver posição manual)
      setTimeout(() => {
        if (quote.parentNode && !quote.dataset.manualPosition) {
          quote.parentNode.removeChild(quote);
          
          // Verificar se precisamos criar uma nova para manter o mínimo
          const remainingVisible = heroQuotesAnimation.querySelectorAll('.hero-quote:not(.dragging):not([data-manual-position="true"])').length;
          if (remainingVisible < 2) {
            createSingleQuote();
          }
        }
      }, duration * 1000);
    }

    // Global mouse move handler
    const handleMouseMove = (e) => {
      if (heroIsDragging && heroDraggedQuote) {
        const newX = e.clientX - heroDragOffset.x;
        const newY = e.clientY - heroDragOffset.y;
        const percentX = (newX / window.innerWidth) * 100;
        const quoteWidth = heroDraggedQuote.offsetWidth;
        const quoteHeight = heroDraggedQuote.offsetHeight;
        const maxX = 100 - (quoteWidth / window.innerWidth * 100);
        const constrainedX = Math.max(0, Math.min(percentX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, window.innerHeight - quoteHeight));
        heroDraggedQuote.style.left = constrainedX + '%';
        heroDraggedQuote.style.top = constrainedY + 'px';
        return;
      }

      const allHeroQuotes = document.querySelectorAll('.hero-quote');
      allHeroQuotes.forEach(function (quote) {
        if (quote.dataset.isPotentialDrag === 'true' && quote.dataset.hasMoved === 'false') {
          const mouseDownX = parseFloat(quote.dataset.mouseDownX);
          const mouseDownY = parseFloat(quote.dataset.mouseDownY);
          const deltaX = Math.abs(e.clientX - mouseDownX);
          const deltaY = Math.abs(e.clientY - mouseDownY);
          if (deltaX > 5 || deltaY > 5) {
            quote.dataset.hasMoved = 'true';
            heroIsDragging = true;
            heroDraggedQuote = quote;
            quote.classList.add('dragging');
            const rect = quote.getBoundingClientRect();
            heroDragOffset.x = e.clientX - rect.left;
            heroDragOffset.y = e.clientY - rect.top;
          }
        }
      });
    };

    // Global mouse up handler
    const handleMouseUp = (e) => {
      if (heroIsDragging && heroDraggedQuote) {
        heroIsDragging = false;
        heroDraggedQuote.classList.remove('dragging');
        heroDraggedQuote.dataset.isPotentialDrag = 'false';
        heroDraggedQuote.dataset.hasMoved = 'false';
        heroDraggedQuote.dataset.manualPosition = 'true';
        heroDraggedQuote = null;
            } else {
              const allHeroQuotes = document.querySelectorAll('.hero-quote.selected');
              allHeroQuotes.forEach(function (quote) {
                if (quote.dataset.hasMoved === 'false') {
                  quote.classList.remove('selected');
                  const originalDuration = parseFloat(quote.dataset.originalDuration) || 20;
                  quote.style.animation = `floatQuote ${originalDuration}s linear forwards`;
                }
              });
            }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Create initial hero quotes (2-3 frases iniciais)
    const initialCount = 2 + Math.floor(Math.random() * 2); // 2 ou 3
    for (let i = 0; i < initialCount; i++) {
      setTimeout(() => createHeroQuote(), i * 2000);
    }

    // Criar novas frases periodicamente, mas respeitando o limite
    const quoteInterval = setInterval(() => {
      const heroQuotesAnimation = document.getElementById('heroQuotesAnimation');
      if (!heroQuotesAnimation) return;
      
      const visibleCount = heroQuotesAnimation.querySelectorAll('.hero-quote:not(.dragging):not([data-manual-position="true"])').length;
      
      // Só criar nova se tiver menos de 5 frases visíveis
      if (visibleCount < 5) {
        createHeroQuote();
      }
    }, 4000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      clearInterval(quoteInterval);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      observer.disconnect();
    };
  }, [lastScrollY]);

  // Efeito para pausar/retomar animações das frases durante o scroll
  useEffect(() => {
    const heroQuotesAnimation = document.getElementById('heroQuotesAnimation');
    if (!heroQuotesAnimation) return;

    if (isScrolling) {
      // Adicionar classe para pausar animações
      heroQuotesAnimation.classList.add('scrolling');
    } else {
      // Remover classe para retomar animações
      heroQuotesAnimation.classList.remove('scrolling');
    }
  }, [isScrolling]);

  return (
    <div className="home-page">
      <header className={`header ${isHeaderVisible ? 'header-visible' : 'header-hidden'}`}>
        <div className="header-content">
          <div className="logo">
            <img src="/logo.png" alt="Crescer Metas Logo" />
          </div>
          <nav className="nav-menu">
            <a href="#features">Recursos</a>
            <a href="#pricing">Planos</a>
            <a href="https://crescer.emp.br/" target="_blank" rel="noopener noreferrer">Outras Soluções</a>
          </nav>
          <div className="header-actions">
            <Link to="/login" className="login-btn">
              <i className="fas fa-sign-in-alt"></i> Login
            </Link>
          </div>
        </div>
      </header>

      <div className="hero-section">
        <div className="hero-quotes-animation" id="heroQuotesAnimation"></div>
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">
              <i className="fas fa-chart-line"></i>
              <span>Sistema Premium de Gestão</span>
            </div>
            <h1 className="hero-title">
              <span className="title-main">Transforme Dados em</span>
              <span className="title-brand">Resultados Extraordinários</span>
            </h1>
            <p className="hero-description">
              A plataforma mais completa para <strong>gestão de metas empresariais</strong>.
              Dashboard avançado com agrupamento inteligente, múltiplas operações e unidades,
              visualização de dados em tempo real com <strong>comparação de ano anterior</strong>
              e analytics que impulsionam decisões estratégicas.
            </p>

            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">300%</div>
                <div className="stat-label">Crescimento Médio</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">24/7</div>
                <div className="stat-label">Monitoramento</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Personalizável</div>
              </div>
            </div>

            <div className="hero-cta">
              <Link to="/signup" className="cta-button primary">
                <i className="fas fa-rocket"></i>
                <span>Começar Grátis</span>
              </Link>
              <a href="#demo" className="cta-button secondary">
                <i className="fas fa-play"></i>
                <span>Ver Demonstração</span>
              </a>
            </div>
          </div>

          <div className="dashboard-preview-hero">
            <div className="preview-card">
              <div className="preview-header">
                <h3>Dashboard ao Vivo</h3>
                <div className="live-indicator">
                  <div className="pulse"></div>
                  <span>Tempo Real</span>
                </div>
              </div>
              <div className="chart-preview">
                <div className="dashboard-image-container">
                  <img src="/dash.png" alt="Dashboard Crescer Metas" className="dashboard-image" />
                  <div className="dashboard-overlay"></div>
                  <div className="dashboard-glow"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="features-section" id="features">
        <div className="section-header">
          <div className="section-badge">
            <i className="fas fa-star"></i>
            <span>Recursos Premium</span>
          </div>
          <h2 className="section-title">
            Por que empresas líderes escolhem <span className="highlight">Crescer Metas</span>?
          </h2>
          <p style={{ color: 'var(--text-light)', fontSize: '1.2rem' }}>
            Gestão completa de múltiplas operações e unidades com inteligência avançada
          </p>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-layer-group"></i>
            </div>
            <h4>Múltiplas Operações</h4>
            <p>Gerencie várias operações simultaneamente com agrupamento inteligente por título, operação ou
              unidade. Visão consolidada ou detalhada conforme sua necessidade.</p>
            <div className="feature-benefits">
              <span>Agrupamento dinâmico</span>
              <span>Visão consolidada</span>
              <span>Drill-down completo</span>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <h4>Analytics Avançado</h4>
            <p>Dashboards em tempo real com gráficos interativos, comparação ano anterior e identificação automática
              de tendências e anomalias.</p>
            <div className="feature-benefits">
              <span>Tempo real</span>
              <span>Comparação histórica</span>
              <span>Insights automáticos</span>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-palette"></i>
            </div>
            <h4>Visualização Inteligente</h4>
            <p>Sistema de cores avançado com paleta distinta por operação e gradiente por unidade. Identificação
              visual instantânea de performance.</p>
            <div className="feature-benefits">
              <span>Cores por operação</span>
              <span>Gradiente por unidade</span>
              <span>Comparação visual</span>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-building"></i>
            </div>
            <h4>Gestão Multi-Unidades</h4>
            <p>Controle completo de múltiplas unidades com métricas individuais e consolidadas. Benchmark automático
              entre unidades.</p>
            <div className="feature-benefits">
              <span>Ilimitadas unidades</span>
              <span>Benchmark automático</span>
              <span>Ranking inteligente</span>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-filter"></i>
            </div>
            <h4>Filtros Avançados</h4>
            <p>Filtragem por operação, unidade, departamento e período. Visualize exatamente o que precisa com
              poucos cliques.</p>
            <div className="feature-benefits">
              <span>Filtros combinados</span>
              <span>Períodos flexíveis</span>
              <span>Salvamento de views</span>
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <i className="fas fa-users-cog"></i>
            </div>
            <h4>Controle de Acesso</h4>
            <p>Sistema robusto de permissões por usuário, operação e departamento. Segurança empresarial completa.
            </p>
            <div className="feature-benefits">
              <span>Permissões granulares</span>
              <span>Auditoria completa</span>
              <span>Multi-departamento</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pricing-section" id="pricing">
        <div className="section-header">
          <div className="section-badge">
            <i className="fas fa-crown"></i>
            <span>Planos Exclusivos</span>
          </div>
          <h2 className="section-title">
            Escolha o plano <span className="highlight">ideal</span> para sua empresa
          </h2>
          <p style={{ color: 'var(--text-light)', fontSize: '1.2rem' }}>
            Comece grátis e evolua conforme cresce
          </p>
        </div>

        <div className="pricing-grid">
          <div className="pricing-card">
            <div className="pricing-header">
              <h3>Gratuito</h3>
              <div className="price">
                <span className="currency">R$</span>
                <span className="amount">0</span>
                <span className="period">/mês</span>
              </div>
              <p>Para começar sua jornada</p>
            </div>
            <div className="pricing-features">
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>1 operação</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>1 unidade</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Dashboard básico</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Até 10 metas</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Gráficos básicos</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Suporte por email</span>
              </div>
            </div>
            <Link to="/signup" className="pricing-cta" data-discover="true">Começar Grátis</Link>
          </div>

          <div className="pricing-card featured">
            <div className="popular-badge">Mais Popular</div>
            <div className="pricing-header">
              <h3>Completo</h3>
              <div className="price">
                <span className="currency">R$</span>
                <span className="amount">397</span>
                <span className="period">/mês</span>
              </div>
              <p>Para empresas em crescimento</p>
            </div>
            <div className="pricing-features">
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Operações ilimitadas</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Unidades ilimitadas</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Dashboard completo com agrupamento</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Metas ilimitadas</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Comparação ano anterior</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Sistema de cores avançado</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Múltiplos usuários</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Controle de acesso granular</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Suporte prioritário</span>
              </div>
            </div>
            <a href="https://wa.me/5583936180508" target="_blank" rel="noopener noreferrer" className="pricing-cta featured">Assinar Agora</a>
          </div>

          <div className="pricing-card">
            <div className="pricing-header">
              <h3>Enterprise</h3>
              <div className="price">
                <span className="price-label">Sob consulta</span>
              </div>
              <p>Solução personalizada</p>
            </div>
            <div className="pricing-features">
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Tudo do Completo</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>API personalizada</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Integrações customizadas</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Dashboard personalizado</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Treinamento dedicado</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>Gerente de conta</span>
              </div>
              <div className="feature-item">
                <i className="fas fa-check"></i>
                <span>SLA garantido</span>
              </div>
            </div>
            <a href="https://wa.me/5583936180508" target="_blank" rel="noopener noreferrer" className="pricing-cta">Falar com Vendas</a>
          </div>
        </div>
      </div>

      <div className="cta-section">
        <div className="cta-content">
          <div className="section-badge">
            <i className="fas fa-fire"></i>
            <span>Oferta Especial</span>
          </div>
          <h2 className="cta-title">
            Transforme suas metas em <span className="gradient-text">Conquistas Reais</span>
          </h2>
          <p style={{ color: 'var(--text-light)', fontSize: '1.2rem', marginBottom: '2rem' }}>
            Junte-se a centenas de empresas que já revolucionaram sua gestão de metas com múltiplas operações e
            analytics avançado.
          </p>

          <div className="cta-buttons">
            <Link to="/signup" className="cta-button primary">
              <i className="fas fa-rocket"></i>
              <span>Começar Grátis Agora</span>
            </Link>
            <a href="https://wa.me/5583936180508" target="_blank" rel="noopener noreferrer" className="cta-button secondary">
              <i className="fab fa-whatsapp"></i>
              <span>Falar com Especialista</span>
            </a>
          </div>

          <div className="trust-indicators">
            <div className="trust-item">
              <i className="fas fa-shield-alt"></i>
              <span>100% Seguro</span>
            </div>
            <div className="trust-item">
              <i className="fas fa-lock"></i>
              <span>Dados Criptografados</span>
            </div>
            <div className="trust-item">
              <i className="fas fa-headset"></i>
              <span>Suporte Premium</span>
            </div>
          </div>
        </div>
      </div>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Crescer Metas</h4>
            <p style={{ color: 'var(--text-light)', marginTop: '1rem' }}>
              Sistema premium de gestão de metas empresariais com dashboard avançado e múltiplas operações.
            </p>
          </div>
          <div className="footer-section">
            <h4>Produto</h4>
            <ul>
              <li><a href="#features">Recursos</a></li>
              <li><a href="#pricing">Planos</a></li>
              <li><a href="#demo">Demonstração</a></li>
              <li><a href="#api">API</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Empresa</h4>
            <ul>
              <li><a href="https://crescer.emp.br/" target="_blank" rel="noopener noreferrer">Crescer</a></li>
              <li><a href="#about">Sobre</a></li>
              <li><a href="#blog">Blog</a></li>
              <li><a href="#contact">Contato</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Suporte</h4>
            <ul>
              <li><a href="#help">Central de Ajuda</a></li>
              <li><a href="#docs">Documentação</a></li>
              <li><a href="https://wa.me/5583936180508" target="_blank" rel="noopener noreferrer">WhatsApp</a></li>
              <li><a href="#status">Status</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2024 Crescer Metas. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
